import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/context/AuthContext'
import { toast } from 'sonner'
import { Mail, Loader2, ArrowLeft, KeyRound } from 'lucide-react'
import { GoogleIcon } from '@/components/icons/GoogleIcon'
import { LogoMonogram } from '@/components/icons/LogoMonogram'
import { ThemeToggle } from '@/components/ThemeToggle'
import { cn } from '@/lib/utils'

type Mode = 'otp' | 'password'
type Step = 'email' | 'code'

export default function LoginPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [mode, setMode] = useState<Mode>('otp')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [step, setStep] = useState<Step>('email')
  const [loading, setLoading] = useState(false)
  const codeInputRef = useRef<HTMLInputElement>(null)
  const keyboardInset = useKeyboardInset()

  const handleInputFocus = useCallback((e: React.FocusEvent<HTMLElement>) => {
    const target = e.target as HTMLElement
    if (!target.matches('input, textarea')) return
    setTimeout(() => {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }, 300)
  }, [])

  useEffect(() => {
    if (user) navigate('/', { replace: true })
  }, [user, navigate])

  useEffect(() => {
    if (step === 'code') {
      setTimeout(() => codeInputRef.current?.focus(), 100)
    }
  }, [step])

  async function handleSendCode(e: React.FormEvent) {
    e.preventDefault()
    if (!email.trim()) return
    setLoading(true)
    const { error } = await supabase.auth.signInWithOtp({
      email: email.trim().toLowerCase(),
    })
    setLoading(false)
    if (error) {
      toast.error(friendlyAuthError(error.message))
      return
    }
    setStep('code')
    toast.success('Code sent! Check your email.')
  }

  async function handleVerifyCode(e: React.FormEvent) {
    e.preventDefault()
    if (code.length < 6) return
    setLoading(true)
    const { error } = await supabase.auth.verifyOtp({
      email: email.trim().toLowerCase(),
      token: code.trim(),
      type: 'email',
    })
    setLoading(false)
    if (error) {
      toast.error('Invalid or expired code. Try again.')
      setCode('')
    }
  }

  async function handleResend() {
    setLoading(true)
    const { error } = await supabase.auth.signInWithOtp({
      email: email.trim().toLowerCase(),
    })
    setLoading(false)
    if (error) {
      toast.error(friendlyAuthError(error.message))
    } else {
      toast.success('New code sent!')
      setCode('')
    }
  }

  async function handleGoogleLogin() {
    setLoading(true)
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/`,
        queryParams: { prompt: 'select_account' },
      },
    })
    if (error) {
      setLoading(false)
      toast.error(friendlyAuthError(error.message))
    }
  }

  async function handlePasswordLogin(e: React.FormEvent) {
    e.preventDefault()
    if (!email.trim() || !password) return
    setLoading(true)
    const { error } = await supabase.auth.signInWithPassword({
      email: email.trim().toLowerCase(),
      password,
    })
    setLoading(false)
    if (error) {
      toast.error(friendlyAuthError(error.message))
    }
  }

  function friendlyAuthError(raw: string): string {
    const msg = raw.toLowerCase()
    if (msg.includes('rate limit') || msg.includes('over_email_send_rate_limit') || msg.includes('too many')) {
      return 'Email rate limit reached. Wait ~1 hour or use another address.'
    }
    if (msg.includes('invalid login credentials') || msg.includes('invalid_credentials')) {
      return 'Wrong email or password.'
    }
    if (msg.includes('invalid') && msg.includes('email')) {
      return 'That email address looks invalid.'
    }
    return raw || 'Something went wrong. Try again.'
  }

  return (
    <div className="min-h-screen bg-background relative">
      {/* Radial violet wash + faint grid holograph behind the glyph */}
      <div className="login-hero" aria-hidden="true" />

      <div className="absolute top-3 right-3 pt-safe z-10">
        <ThemeToggle />
      </div>

      <div
        className="relative z-[1] min-h-screen flex flex-col items-center justify-center px-7 py-10"
        style={{ paddingBottom: `calc(2.5rem + ${keyboardInset}px)` }}
        onFocus={handleInputFocus}
      >
        <div className="w-full max-w-sm space-y-7">
          {/* Mark: glyph + name + tagline */}
          <div className="flex flex-col items-center gap-3 pt-4">
            <div className="w-[58px] h-[58px] rounded-[20px] bg-foreground text-background grid place-items-center">
              <LogoMonogram size={34} />
            </div>
            <h1 className="text-[34px] tracking-[-0.03em] text-foreground leading-none font-semibold">
              TourFlow
            </h1>
            <p className="text-sm text-muted-foreground text-center">
              Tour management for touring crews
            </p>
          </div>

          {/* Google */}
          <div className="space-y-3">
            <button
              type="button"
              onClick={handleGoogleLogin}
              disabled={loading}
              className="login-btn primary"
            >
              <GoogleIcon className="w-5 h-5" />
              Continue with Google
            </button>
            <div className="login-divider">or</div>
          </div>

          {mode === 'password' ? (
            <form onSubmit={handlePasswordLogin} className="space-y-4">
              <div className="login-field">
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  type="email"
                  className="login-input"
                  placeholder="you@gmail.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  autoComplete="email"
                  autoCapitalize="none"
                  autoFocus
                  required
                />
              </div>
              <div className="login-field">
                <label htmlFor="password">Password</label>
                <input
                  id="password"
                  type="password"
                  className="login-input"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                />
              </div>
              <button
                type="submit"
                className="login-btn accent"
                disabled={loading || !email.trim() || !password}
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
                {loading ? 'Signing in...' : 'Sign in'}
              </button>
              <p className="login-footnote">
                <button
                  type="button"
                  onClick={() => { setMode('otp'); setPassword('') }}
                >
                  Use a sign-in code instead
                </button>
              </p>
            </form>
          ) : step === 'email' ? (
            <form onSubmit={handleSendCode} className="space-y-4">
              <div className="login-field">
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  type="email"
                  className="login-input"
                  placeholder="you@gmail.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  autoComplete="email"
                  autoCapitalize="none"
                  autoFocus
                  required
                />
              </div>
              <button
                type="submit"
                className="login-btn accent"
                disabled={loading || !email.trim()}
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                {loading ? 'Sending...' : 'Send sign-in code'}
              </button>
              <p className="login-footnote">
                We'll send a sign-in code to your email. No password needed.
              </p>
              <p className="login-footnote">
                <button type="button" onClick={() => setMode('password')}>
                  Sign in with password instead
                </button>
              </p>
            </form>
          ) : (
            <div className="space-y-4">
              <button
                onClick={() => { setStep('email'); setCode('') }}
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>

              <div className="text-center space-y-1">
                <p className="font-medium text-foreground">Enter your code</p>
                <p className="text-sm text-muted-foreground">
                  We sent a code to <span className="font-medium text-foreground">{email}</span>
                </p>
              </div>

              <form onSubmit={handleVerifyCode} className="space-y-4">
                <input
                  ref={codeInputRef}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={8}
                  placeholder="000000"
                  value={code}
                  onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                  className={cn('login-input mono text-center text-2xl tracking-[0.3em]', 'h-14')}
                  autoComplete="one-time-code"
                />
                <button
                  type="submit"
                  className="login-btn accent"
                  disabled={loading || code.length < 6}
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  {loading ? 'Verifying...' : 'Verify code'}
                </button>
              </form>

              <div className="text-center">
                <button
                  onClick={handleResend}
                  disabled={loading}
                  className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground transition-colors"
                >
                  Didn't get the code? Resend
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// Tracks iOS keyboard height via visualViewport. In standalone PWA, the layout
// viewport doesn't resize when the keyboard opens, so we measure the delta and
// expose it so the caller can add bottom padding — letting inputs scroll above
// the keyboard instead of sitting behind it.
function useKeyboardInset() {
  const [inset, setInset] = useState(0)
  useEffect(() => {
    const vv = window.visualViewport
    if (!vv) return
    const onChange = () => {
      const delta = Math.max(0, window.innerHeight - vv.height - vv.offsetTop)
      setInset(delta)
    }
    onChange()
    vv.addEventListener('resize', onChange)
    vv.addEventListener('scroll', onChange)
    return () => {
      vv.removeEventListener('resize', onChange)
      vv.removeEventListener('scroll', onChange)
    }
  }, [])
  return inset
}
